import * as React from 'react';

import { Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend,
  ChartData, ChartOptions } from 'chart.js';
ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
import { Line } from 'react-chartjs-2';

import type { ISeriesConfig } from './types';
import { ChartLegend } from './ChartLegend';

interface IChartRendererProps {
  /** Number of days to display on the x-axis. */
  days: number;
  /** Array of series configurations defining the data and appearance of each line. */
  series: ISeriesConfig[];
  /** Height of the chart container (number for pixels, or CSS string like "50vh"). */
  height: number | string;
  /**
   * Color for text elements (axis labels, tick values, legend).
   * @default '#333'
   */
  textColor?: string;
  /**
   * Color for grid lines and axis lines.
   * @default 'rgba(0, 0, 0, 0.1)'
   */
  gridColor?: string;
  /**
   * Width of the data lines in pixels.
   * @default 1
   */
  lineWidth?: number;
}

/**
 * Calculates a "nice" step size for axis ticks, producing round numbers
 * like 1, 2, 5, 10, 20, 50, 100, etc.
 *
 * @param range - The total range of values to cover
 * @param targetTicks - The approximate number of ticks desired
 * @returns A "nice" step value that divides the range into readable intervals
 */
const getNiceStep = (range: number, targetTicks: number): number => {
  const rawStep = range / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceStep: number;
  if (normalized <= 1) niceStep = 1;
  else if (normalized <= 2) niceStep = 2;
  else if (normalized <= 5) niceStep = 5;
  else niceStep = 10;
  return niceStep * magnitude;
};

/**
 * Ref type for accessing the chart instance. Spelled out from chart.js rather
 * than imported as react-chartjs-2's ChartJSOrUndefined: that type lives in
 * dist/types.d.ts, which the package's `exports` map does not expose, so the
 * deep import resolved only under the old node10 module resolution.
 */
type ChartRef = ChartJS<"line"> | undefined;

/**
 * Internal component that renders the actual Chart.js line chart.
 *
 * This component handles:
 * - Responsive width tracking for dynamic tick calculation
 * - Building Chart.js datasets from series configurations
 * - Creating multiple Y-axes (left and right positioned)
 * - Generating "nice" X-axis tick values (0, 100, 200, etc.)
 * - Customizable colors for text and grid elements
 */
const ChartRenderer = React.forwardRef<ChartRef, IChartRendererProps>((props, ref) => {
  const textColor = props.textColor ?? '#333';
  const gridColor = props.gridColor ?? 'rgba(0, 0, 0, 0.1)';
  const lineWidth = props.lineWidth ?? 1;
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(300);
  const [hiddenIndices, setHiddenIndices] = React.useState<Set<number>>(new Set());

  const handleToggle = (index: number) => {
    setHiddenIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const getData = (): ChartData<"line"> => {
    return {
      datasets: props.series.map((s, idx) => {
        const hideBefore = s.hideBeforeDay;
        return {
          label: s.label,
          data: hideBefore !== undefined ? s.data.filter(d => d.x >= hideBefore) : s.data,
          borderColor: s.color,
          backgroundColor: s.color,
          pointRadius: 0,
          borderWidth: lineWidth,
          yAxisID: `axis${idx}`,
          hidden: hiddenIndices.has(idx)
        };
      })
    };
  };

  const getScales = (): ChartOptions<"line">["scales"] => {
    const scales: ChartOptions<"line">["scales"] = {
      x: {
        type: 'linear',
        title: { display: true, text: "Culture Time (days)", font: { size: 12 }, color: textColor },
        ticks: { font: { size: 10 }, color: textColor },
        grid: { color: gridColor },
        border: { color: gridColor },
        min: 0,
        max: props.days - 1,
        afterBuildTicks: (axis) => {
          const step = getNiceStep(props.days, Math.max(2, Math.floor(containerWidth / 50)));
          const ticks: { value: number }[] = [];
          for (let v = 0; v <= props.days - 1; v += step) {
            ticks.push({ value: v });
          }
          axis.ticks = ticks;
        }
      }
    };

    // Build Y-axis scales from series configurations
    props.series.forEach((s, idx) => {
      // Hidden series get a hidden axis
      if (hiddenIndices.has(idx)) {
        scales[`axis${idx}`] = { display: false };
        return;
      }

      // Check if this is the first VISIBLE series on this position
      const isFirstOnPosition = !props.series.slice(0, idx).some(
        (prev, prevIdx) => prev.axis.position === s.axis.position && !hiddenIndices.has(prevIdx)
      );

      const axisTitle = s.unit ? `${s.axis.title} (${s.unit})` : s.axis.title;

      scales[`axis${idx}`] = {
        type: "linear",
        position: s.axis.position,
        title: { display: true, text: axisTitle, font: { size: 12 }, color: textColor },
        ticks: { font: { size: 10 }, color: textColor },
        grid: { color: gridColor, drawOnChartArea: isFirstOnPosition },
        border: { color: gridColor },
        min: s.axis.min,
        max: s.axis.max,
        afterFit: (axis) => { axis.width = s.axis.width; }
      };
    });

    return scales;
  };

  return (
    <div ref={containerRef} style={{ height: props.height, display: 'flex', flexDirection: 'column' }}>
      <ChartLegend
        series={props.series}
        textColor={textColor}
        hiddenIndices={hiddenIndices}
        onToggle={handleToggle}
      />
      <div style={{ flex: 1, minHeight: 0 }}>
        <Line
          ref={ref}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            font: { size: 10 },
            scales: getScales(),
            plugins: {
              legend: {
                display: false,
              },
            title: {
              display: false,
              text: '',
            },
            tooltip: {
              usePointStyle: true,
              boxWidth: 6,
              boxHeight: 6,
              boxPadding: 6,
              callbacks: {
                title: (items) => `Culture day ${Math.round(items[0].parsed.x)}`,
                label: (item) => {
                  const series = props.series[item.datasetIndex];
                  const decimals = series.tooltipDecimals ?? 0;
                  const unit = series.unit ?? '';
                  const value = item.parsed.y.toFixed(decimals);
                  return `${series.label}: ${value} ${unit}`;
                }
              }
            }
          },
        }}
        data={getData()}
      />
      </div>
    </div>
  );
});

export { ChartRenderer, IChartRendererProps, ChartRef }
